import React, { useState } from 'react';
import { MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import { addRelistingNote } from '../../../api/dashboard';
import { formatMelbourneShortDate } from '../../../utils/melbourneTime';

/**
 * Notes on a re-listing opportunity that every agent sees on the row (Paul
 * Jimoulis CRM-2026-00116, 20 Oct 2026: "make a option that you can select to
 * write a note so other agents in the office can see that the lead has been
 * spoken to so there isnt any double ups"). The row stays on the list; Add Lead
 * is still what takes it off.
 *
 * `notes` and `onAdded` live in the row so the Note button and this panel share them.
 */
export const RelistingNoteButton = ({ count, open, onToggle }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onToggle();
    }}
    aria-expanded={open}
    title="Leave a note other agents can see"
    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-gray-600 border border-gray-200 bg-white hover:border-gray-300 whitespace-nowrap"
  >
    <MessageSquare className="w-3.5 h-3.5" />
    Note{count ? ` (${count})` : ''}
  </button>
);

const NoteLine = ({ note }) => (
  <div className="text-[11px] text-gray-600 leading-snug">
    <span className="font-semibold text-gray-800">{note.author_name || 'An agent'}</span>
    <span className="text-gray-400"> · {formatMelbourneShortDate(note.created_at)}: </span>
    <span className="whitespace-pre-wrap break-words">{note.content}</span>
  </div>
);

const RelistingNotes = ({ campaignId, notes, open, onAdded, onClose }) => {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  if (!open && !notes.length) return null;

  const save = async () => {
    const content = text.trim();
    if (!content) {
      toast.error('Write the note first.');
      return;
    }
    setSaving(true);
    try {
      const res = await addRelistingNote(campaignId, content);
      onAdded(res.data);
      setText('');
      toast.success('Note saved. Every agent can see it on this property.');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to save the note.');
    } finally {
      setSaving(false);
    }
  };

  // Closed: just the latest note, so the list stays compact.
  if (!open) {
    const last = notes[notes.length - 1];
    return (
      <div className="mt-2 ml-12 rounded-lg bg-amber-50 border border-amber-100 px-3 py-1.5">
        <NoteLine note={last} />
        {notes.length > 1 && (
          <div className="text-[10px] text-gray-400 mt-0.5">+{notes.length - 1} earlier {notes.length === 2 ? 'note' : 'notes'}</div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-2 ml-12 rounded-lg bg-gray-50 border border-gray-100 p-3 space-y-2" onClick={(e) => e.stopPropagation()}>
      {notes.length > 0 && (
        <div className="space-y-1.5">
          {notes.map((n) => (
            <NoteLine key={n.id} note={n} />
          ))}
        </div>
      )}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        aria-label="Note for other agents"
        placeholder="e.g. Spoke to the owner, not selling until spring"
        className="w-full min-h-[60px] border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
      />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-3 py-1.5 text-[12px] font-semibold text-gray-500 hover:text-gray-700">
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white bg-[#55C5D0] hover:bg-[#45b5c0] disabled:opacity-60"
        >
          {saving ? 'Saving...' : 'Save note'}
        </button>
      </div>
    </div>
  );
};

export default RelistingNotes;
